import type { State } from '@/reducers/types';
import { ReportType } from '@/types';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { logout } from '../auth';
import {
  createArit,
  getIms,
  getReport,
  getReportsList,
  getReportTypes,
  removeReport,
  retrieveDocument,
  searchReports,
} from './actions';

const chatsAdapter = createEntityAdapter<any>({
  sortComparer: (a, b) => a.id.localeCompare(b.id),
});

interface ChatsState {
  messages: any;
  questions: any;
  reports: {
    data: any[];
    total: number;
  };
  loading: boolean;
  searchResults: any;
  loadingSearch: boolean;
  filterValue: ReportType[];
  reportTypes: string[];
}

const initialState = chatsAdapter.getInitialState<ChatsState>({
  messages: null,
  questions: null,
  reports: {
    data: [],
    total: 0,
  },
  loading: false,
  searchResults: null,
  loadingSearch: false,
  filterValue: [],
  reportTypes: [],
});

const { name, reducer, actions } = createSlice({
  name: 'chats',
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.messages = null;
    },
    setMessages: (state, action) => {
      state.messages = action.payload;
    },
    toggleFilterValue: (state, action) => {
      const type = action.payload;
      if (state.filterValue.includes(type)) {
        state.filterValue = state.filterValue.filter((t) => t !== type);
      } else {
        state.filterValue.push(type);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReportsList.fulfilled, (state, action) => {
        if (action.payload.reports[0]) {
          state.messages = { output: action.payload.reports[0] };
        }
        if (action.meta.arg.lastReportId) {
          state.reports.data = [...state.reports.data, ...action.payload.reports];
        } else {
          state.reports.data = action.payload.reports;
        }
        state.reports.total = action.payload.total;
        state.loading = false;
      })
      .addCase(getReportsList.pending, (state, action) => {
        if (!action.meta.arg.lastReportId) {
          state.loading = true;
        }
      })
      .addCase(removeReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(searchReports.pending, (state) => {
        state.searchResults = null;
        state.loadingSearch = true;
      })
      .addCase(searchReports.fulfilled, (state, action) => {
        state.searchResults = action.payload.reports;
        state.loadingSearch = false;
      })
      .addCase(retrieveDocument.pending, (state) => {
        state.messages = null;
      })
      .addCase(retrieveDocument.fulfilled, (state, action) => {
        if (!!action.payload.output.answer && action.payload.title !== 'Title generate') {
          state.messages = action.payload;
        }
      })
      .addCase(createArit.pending, (state) => {
        state.messages = null;
      })
      .addCase(createArit.fulfilled, (state, action) => {
        state.messages = action.payload;
      })
      .addCase(getIms.pending, (state) => {
        state.messages = null;
      })
      .addCase(getIms.fulfilled, (state, action) => {
        state.messages = action.payload;
      })
      .addCase(getReport.pending, (state) => {
        state.messages = null;
      })
      .addCase(getReport.fulfilled, (state, action) => {
        state.messages = action.payload;
      })
      .addCase(getReportTypes.fulfilled, (state, action) => {
        state.reportTypes = action.payload;
      })
      .addCase(logout.fulfilled, () => initialState);
  },
});

const { clearMessages, setMessages, toggleFilterValue } = actions;

export { clearMessages, setMessages, name, reducer, toggleFilterValue };

export const selectCurrentMessages = (state: State) => state[name].messages;
export const selectCurrentQuestions = (state: State) => state[name].questions;
export const selectCurrentReports = (state: State) => state[name].reports.data;
export const selectCurrentReportsTotal = (state: State) => state[name].reports.total;
export const selectLoading = (state: State) => state[name].loading;
export const selectSearchResults = (state: State) => state[name].searchResults;
export const selectLoadingSearch = (state: State) => state[name].loadingSearch;
export const selectFilterValue = (state: State) => state[name].filterValue;
export const selectReportTypes = (state: State) => state[name].reportTypes;
